import { Injectable, signal } from '@angular/core';
import { BannerAction, BannerActionTone, BannerTone } from '../../shared/banner.component';

/** A second, optional button in the shell banner's action row ("Enable editing"). */
export interface ShellBannerAction {
  label: string;
  handler: () => void;
  /** `ext-banner` action tone; the outlined one when omitted (the main action keeps the fill). */
  tone?: BannerActionTone;
  /** Material Symbols name shown before the label. */
  icon?: string;
}

/**
 * What the shell's banner slot (between the top bar and the tab nav) shows. Strings are already
 * translated — the caller owns the i18n key and its params, the shell only renders.
 */
export interface ShellBanner {
  tone: BannerTone;
  text: string;
  /** Material Symbols name; the banner's per-tone default when omitted. */
  icon?: string;
  /** Optional main action at the end of the row ("Back to admin"). */
  actionLabel?: string;
  action?: () => void;
  /** Optional secondary action, rendered before the main one ("Enable editing" / "Disable editing"). */
  secondaryAction?: ShellBannerAction;
}

/** The banner's buttons in row order — the secondary one first, the main one last — and their
 *  handlers by the same index: what the shell hands to `ext-banner [actions]`, whose `actionClick`
 *  gives the index back. */
export function shellBannerActions(banner: ShellBanner | null): { buttons: BannerAction[]; handlers: (() => void)[] } {
  const buttons: BannerAction[] = [];
  const handlers: (() => void)[] = [];
  if (banner?.secondaryAction) {
    const s = banner.secondaryAction;
    buttons.push({ label: s.label, tone: s.tone ?? 'outline', ...(s.icon ? { icon: s.icon } : {}) });
    handlers.push(s.handler);
  }
  if (banner?.actionLabel) {
    buttons.push({ label: banner.actionLabel, tone: 'primary' });
    handlers.push(() => banner.action?.());
  }
  return { buttons, handlers };
}

/**
 * App-wide banner slot in the shell (F0, WI #3294). Whoever needs to say something above every
 * screen — F7's admin "Viewing the product as …" bar is the first — sets it here and clears it
 * when done; the shell never has to change again. One banner at a time: a later `show()`
 * replaces the current one.
 */
@Injectable({ providedIn: 'root' })
export class ShellBannerService {
  private readonly current = signal<ShellBanner | null>(null);

  /** The banner on screen, `null` when the slot is empty. */
  readonly shellBanner = this.current.asReadonly();

  show(banner: ShellBanner): void {
    this.current.set(banner);
  }

  clear(): void {
    this.current.set(null);
  }
}
